/**
 * Projetos — seleciona um ou mais projetos do DinaBox (via proxy) para montar
 * a lista de compras. Carrega TODOS os projetos (paginas em paralelo, com teto)
 * e faz a busca LOCAL por trecho (substring) em: nome do projeto + nome do
 * cliente + código do projeto — instantânea e cobrindo todos os projetos, não
 * só a página atual. Multi-seleção + barra de ação fixa, erros amigáveis.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiAlertTriangle, FiCheck, FiChevronLeft, FiChevronRight,
  FiClock, FiRefreshCw, FiSearch, FiSettings,
} from "react-icons/fi";

import EntityHeader from "../../components/EntityHeader";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { buildItemsFromProject } from "./lib/parseProjeto";
import { listarProjetos, obterProjeto } from "../../services/revisorFerragens";
import { formatoNaoReconhecido, projetosDaResposta } from "./projetosApi";
import { useRevisor } from "./context";
import {
  EmptyBox, Page, ProjectCard, ProjectCheck, ProjectId, ProjectMeta,
  ProjectTitle, ProjectsGrid, SearchWrap, SelectionBar, Toolbar,
} from "./styles";

const STATUS_OPCOES = [
  ["", "Todos os status"],
  ["producao", "Produção"],
  ["rascunho", "Rascunho"],
  ["fechado", "Fechado"],
];

const PER_PAGE = 20;        // itens por página na grade (paginação local)
const POR_REQUISICAO = 20;  // enum máximo aceito pela API DinaBox
const MAX_PAGINAS = 30;     // teto de carregamento (~600 projetos)

function statusLabel(s) {
  const map = { production: "Produção", producao: "Produção", draft: "Rascunho",
    rascunho: "Rascunho", fechado: "Fechado", closed: "Fechado" };
  return map[(s || "").toLowerCase()] || s || "—";
}

/* Enxuga o JSON cru do DinaBox: woodwork/holes ja viraram `items`, entao
   guardamos so os metadados usados pelo Editor/impressao. Evita estourar a
   cota do sessionStorage (persistencia do contexto) em projetos grandes. */
function metaDoProjeto(raw) {
  return {
    project_id: raw?.project_id,
    project_description: raw?.project_description,
    project_customer_name: raw?.project_customer_name,
    project_client_name: raw?.project_client_name,
    project_author_name: raw?.project_author_name,
    project_status: raw?.project_status,
  };
}

function normalizar(s) {
  // remove acentos (combining diacritical marks) para busca tolerante
  return String(s || "").toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

/** Filtro local por trecho: nome do projeto + nome do cliente + código (id). */
function filtrar(projetos, busca) {
  const q = normalizar(busca.trim());
  if (!q) return projetos;
  return projetos.filter((p) =>
    normalizar(p.project_description).includes(q)
    || normalizar(p.project_client_name).includes(q)
    || normalizar(p.project_customer_name).includes(q)
    || normalizar(p.project_id).includes(q),
  );
}

export default function Projetos() {
  const { request } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { setAmbientes } = useRevisor();

  const [todos, setTodos] = useState([]);
  const [totalServidor, setTotalServidor] = useState(0);
  const [page, setPage] = useState(1);
  const [busca, setBusca] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [erroCredencial, setErroCredencial] = useState(false);
  const [erro, setErro] = useState(null); // { tipo, msg }
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [carregandoSel, setCarregandoSel] = useState(false);

  const reqIdRef = useRef(0);

  const carregar = useCallback(
    async (st = "") => {
      const meu = ++reqIdRef.current;
      setLoading(true);
      setErro(null);
      setErroCredencial(false);
      try {
        const primeira = await listarProjetos(request, { p: 1, q: POR_REQUISICAO, status: st });
        let all = projetosDaResposta(primeira);
        const total = primeira.total || all.length;
        // O servidor diz que ha projetos mas nada foi lido => o formato da
        // resposta mudou. Gritar aqui evita repetir o episodio de 2026-08-19,
        // em que a tela ficou vazia e ninguem sabia por que.
        if (formatoNaoReconhecido(primeira, all.length)) {
          const falha = new Error("formato-desconhecido");
          falha.formatoDesconhecido = true;
          throw falha;
        }
        const totalPaginas = Math.min(Math.ceil(total / POR_REQUISICAO) || 1, MAX_PAGINAS);
        if (totalPaginas > 1) {
          const restantes = await Promise.all(
            Array.from({ length: totalPaginas - 1 }, (_, i) =>
              listarProjetos(request, { p: i + 2, q: POR_REQUISICAO, status: st })),
          );
          restantes.forEach((d) => { all = all.concat(projetosDaResposta(d)); });
        }
        if (meu !== reqIdRef.current) return;
        setTodos(all);
        setTotalServidor(total);
      } catch (err) {
        if (meu !== reqIdRef.current) return;
        if (err?.formatoDesconhecido) {
          setErro({
            tipo: "servico",
            msg: "O servidor respondeu com projetos, mas em um formato que esta"
              + " tela não reconhece. Avise o suporte.",
          });
        } else if (err?.status === 409 && err?.payload?.credencial_ausente) {
          setErroCredencial(true);
        } else if (err?.payload?.categoria === "credencial") {
          setErro({ tipo: "credencial", msg: err.payload.detalhe || "Credenciais do DinaBox inválidas." });
        } else {
          setErro({
            tipo: "servico",
            msg: "Não foi possível carregar os projetos do DinaBox agora. Tente novamente em instantes.",
          });
        }
        setTodos([]);
        setTotalServidor(0);
      } finally {
        if (meu === reqIdRef.current) setLoading(false);
      }
    },
    [request],
  );

  // Recarrega do servidor só quando o status muda (e no mount). A busca é local.
  useEffect(() => {
    setPage(1);
    carregar(status);
  }, [status, carregar]);

  // Busca local: volta pra página 1 da grade ao mudar o termo.
  useEffect(() => {
    setPage(1);
  }, [busca]);

  const filtrados = useMemo(() => filtrar(todos, busca), [todos, busca]);
  const pages = Math.max(1, Math.ceil(filtrados.length / PER_PAGE));
  const pageAtual = Math.min(page, pages);
  const paginaItens = filtrados.slice((pageAtual - 1) * PER_PAGE, pageAtual * PER_PAGE);
  const truncado = todos.length < totalServidor;

  function toggle(id) {
    setSelecionados((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  async function carregarSelecionados() {
    const ids = Array.from(selecionados);
    if (!ids.length) return;
    setCarregandoSel(true);
    try {
      const settled = await Promise.allSettled(
        ids.map((id) => obterProjeto(request, id).then((raw) => ({ id, raw }))),
      );
      const ok = settled.filter((s) => s.status === "fulfilled").map((s) => s.value);
      const falhas = settled.length - ok.length;

      if (!ok.length) {
        const rejeitado = settled.find((s) => s.status === "rejected");
        const cat = rejeitado?.reason?.payload?.categoria;
        toast.error(
          cat === "credencial"
            ? "Credenciais do DinaBox inválidas. Verifique em Configurações > DinaBox."
            : "Não foi possível carregar o(s) ambiente(s) agora. Tente novamente em instantes.",
        );
        return;
      }
      if (falhas > 0) {
        toast.warning(`${falhas} ambiente(s) não puderam ser carregados; segui com ${ok.length}.`);
      }
      setAmbientes(
        ok.map(({ id, raw }) => ({
          projectId: id,
          raw: metaDoProjeto(raw),
          items: buildItemsFromProject(raw),
        })),
      );
      navigate("editor");
    } finally {
      setCarregandoSel(false);
    }
  }

  return (
    <Page>
      <EntityHeader
        title="Revisor de Ferragens"
        subtitle="Selecione um ou mais ambientes do DinaBox para montar a lista de compras."
        primaryAction={{
          label: "Histórico",
          icon: <FiClock size={14} />,
          onClick: () => navigate("historico"),
        }}
        actions={[
          { label: "Recarregar", icon: <FiRefreshCw size={14} />, onClick: () => carregar(status) },
        ]}
      />

      {erroCredencial ? (
        <EmptyBox>
          <FiSettings size={34} style={{ marginBottom: 10, color: "var(--ink-3)" }} />
          <div style={{ fontWeight: 600, color: "var(--ink)", marginBottom: 6 }}>
            Credencial DinaBox não configurada
          </div>
          <div style={{ maxWidth: 420, margin: "0 auto 16px" }}>
            Cadastre seu usuário e senha do DinaBox para buscar seus projetos.
          </div>
          <button className="btn btn--primary" onClick={() => navigate("/configuracoes/dinabox")}>
            Configurar DinaBox
          </button>
        </EmptyBox>
      ) : (
        <>
          <Toolbar>
            <SearchWrap>
              <div style={{ position: "relative" }}>
                <FiSearch
                  size={15}
                  style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--ink-3)" }}
                />
                <input
                  className="input"
                  style={{ paddingLeft: 34 }}
                  placeholder="Buscar por projeto, cliente ou código…"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </div>
            </SearchWrap>
            <select
              className="input"
              style={{ maxWidth: 200 }}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPCOES.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </Toolbar>

          {loading ? (
            <EmptyBox>Carregando projetos…</EmptyBox>
          ) : erro ? (
            <EmptyBox>
              <FiAlertTriangle size={32} style={{ marginBottom: 10, color: "var(--warning-text)" }} />
              <div style={{ color: "var(--ink)", marginBottom: 14, maxWidth: 440, marginLeft: "auto", marginRight: "auto" }}>
                {erro.msg}
              </div>
              {erro.tipo === "credencial" ? (
                <button className="btn btn--primary" onClick={() => navigate("/configuracoes/dinabox")}>
                  Configurar DinaBox
                </button>
              ) : (
                <button className="btn btn--secondary" onClick={() => carregar(status)}>
                  Tentar de novo
                </button>
              )}
            </EmptyBox>
          ) : filtrados.length === 0 ? (
            <EmptyBox>
              {busca.trim()
                ? `Nenhum projeto contém "${busca.trim()}" no nome, cliente ou código.`
                : "Nenhum projeto encontrado."}
            </EmptyBox>
          ) : (
            <>
              <ProjectsGrid>
                {paginaItens.map((p) => {
                  const id = p.project_id;
                  const sel = selecionados.has(id);
                  return (
                    <ProjectCard key={id} type="button" $selected={sel} onClick={() => toggle(id)}>
                      <ProjectCheck $selected={sel}>
                        {sel ? <FiCheck size={13} /> : null}
                      </ProjectCheck>
                      <ProjectTitle>{p.project_description || "(sem nome)"}</ProjectTitle>
                      <ProjectId>#{id}</ProjectId>
                      <ProjectMeta>
                        <span>Cliente: {p.project_client_name || p.project_customer_name || "—"}</span>
                        <span>Autor: {p.project_author_name || "—"}</span>
                        <span>Status: {statusLabel(p.project_status)}</span>
                      </ProjectMeta>
                    </ProjectCard>
                  );
                })}
              </ProjectsGrid>

              {pages > 1 ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 22, color: "var(--ink-2)", fontSize: 13 }}>
                  <button
                    className="btn btn--secondary btn--sm"
                    disabled={pageAtual <= 1}
                    onClick={() => setPage(pageAtual - 1)}
                  >
                    <FiChevronLeft size={14} />
                  </button>
                  <span>Página {pageAtual} de {pages} · {filtrados.length} projeto{filtrados.length !== 1 ? "s" : ""}</span>
                  <button
                    className="btn btn--secondary btn--sm"
                    disabled={pageAtual >= pages}
                    onClick={() => setPage(pageAtual + 1)}
                  >
                    <FiChevronRight size={14} />
                  </button>
                </div>
              ) : null}

              {truncado ? (
                <div style={{ textAlign: "center", marginTop: 14, fontSize: 12, color: "var(--ink-3)" }}>
                  Mostrando os primeiros {todos.length} de {totalServidor} projetos. Use o filtro de status para refinar.
                </div>
              ) : null}
            </>
          )}
        </>
      )}

      {selecionados.size > 0 ? (
        <SelectionBar>
          <span style={{ fontSize: 14, fontWeight: 500 }}>
            {selecionados.size} {selecionados.size === 1 ? "selecionado" : "selecionados"}
          </span>
          <button
            className="btn btn--secondary btn--sm"
            onClick={() => setSelecionados(new Set())}
            disabled={carregandoSel}
          >
            Limpar
          </button>
          <button
            className="btn btn--primary"
            onClick={carregarSelecionados}
            disabled={carregandoSel}
          >
            {carregandoSel ? "Carregando…" : "Carregar lista de compras"}
          </button>
        </SelectionBar>
      ) : null}
    </Page>
  );
}
